/**
 * Client-side UUIDv7 generator (RFC 9562) — used as the idempotency key for
 * POST /api/submit (docs/ARCHITECTURE.md §6.2). No dependency on Node's
 * crypto module: uses Web Crypto (`crypto.getRandomValues`), available in
 * every browser this client targets.
 */

function randomBytes(n: number): Uint8Array {
  const bytes = new Uint8Array(n);
  crypto.getRandomValues(bytes);
  return bytes;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function generateUuidV7(): string {
  const unixMs = Date.now();
  const timeBytes = new Uint8Array(6);
  for (let i = 5; i >= 0; i--) {
    // Uint8Array assignment truncates toward zero then wraps mod 256, which
    // is exactly the byte-extraction this needs.
    timeBytes[i] = unixMs / Math.pow(256, 5 - i);
  }

  const rand = randomBytes(10);
  // Version 7 in the top nibble of byte 6.
  rand[0] = (rand[0] & 0x0f) | 0x70;
  // RFC 4122 variant (10xx) in the top bits of byte 8 (rand[2] here).
  rand[2] = (rand[2] & 0x3f) | 0x80;

  const hex = toHex(timeBytes) + toHex(rand);
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join("-");
}

/** Decodes the 48-bit unix-ms timestamp embedded in a UUIDv7 string. */
export function extractUuidV7Timestamp(uuid: string): number {
  const hex = uuid.replace(/-/g, "").slice(0, 12);
  return parseInt(hex, 16);
}
